/**
 * Inline-editable profile section for /account. Lets the user update
 * name, email and phone. Each successful save bumps the AuthContext so
 * the navbar reflects the new name immediately.
 */
import { useEffect, useState } from "react";
import { Check, Loader2, Mail, Phone, UserCircle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

function Field({ icon: Icon, label, testid, children }) {
  return (
    <label className="block" data-testid={testid}>
      <span className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500">
        <Icon className="h-3.5 w-3.5" />
        {label}
      </span>
      <span className="mt-1.5 block">{children}</span>
    </label>
  );
}

export default function ProfileEditor({ user, onUpdated }) {
  const [name, setName] = useState(user.name || "");
  const [email, setEmail] = useState(user.email || "");
  const [phone, setPhone] = useState(user.phone || "");
  const [saving, setSaving] = useState(false);

  // Refresh from server once on mount so we get the current phone value
  // (AuthContext only knows what /auth/me returns — phone may not be there yet).
  useEffect(() => {
    let cancelled = false;
    api
      .get("/users/me/profile")
      .then((res) => {
        if (cancelled) return;
        setName(res.data.name || "");
        setEmail(res.data.email || "");
        setPhone(res.data.phone || "");
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const dirty =
    (name || "") !== (user.name || "") ||
    (email || "").toLowerCase() !== (user.email || "").toLowerCase() ||
    (phone || "") !== (user.phone || "");

  const onSave = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/users/me/profile", {
        name: name.trim(),
        email: email.trim().toLowerCase(),
        phone: phone.trim() || null,
      });
      toast.success(
        data.changes?.length
          ? `Updated ${data.changes.join(", ")}`
          : "No changes",
      );
      onUpdated?.();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) ||
          "Couldn't save your changes.",
      );
    } finally {
      setSaving(false);
    }
  };

  const inputCls =
    "w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-base text-slate-900 focus:outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100";

  return (
    <div
      className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-6"
      data-testid="account-profile-editor"
    >
      <Field icon={UserCircle} label="Display name" testid="account-edit-name">
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className={inputCls}
          data-testid="account-edit-name-input"
        />
      </Field>
      <Field icon={Mail} label="Email" testid="account-edit-email">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className={inputCls}
          data-testid="account-edit-email-input"
        />
      </Field>
      <Field icon={Phone} label="Mobile phone" testid="account-edit-phone">
        <input
          type="tel"
          value={phone}
          placeholder="+61 4xx xxx xxx"
          onChange={(e) => setPhone(e.target.value)}
          className={inputCls}
          data-testid="account-edit-phone-input"
        />
      </Field>

      <div className="sm:col-span-2 flex items-center justify-end">
        <button
          type="button"
          onClick={onSave}
          disabled={!dirty || saving}
          className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-40"
          data-testid="account-edit-save-button"
        >
          {saving ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Check className="h-4 w-4" />
          )}
          Save changes
        </button>
      </div>
    </div>
  );
}
